import { useQueryParam } from '@/hooks/useQueryParam';
import { volunteerApi } from '@/api/volunteer';
import {
  Button,
  Group,
  Container,
  Alert,
  Title,
  Text,
  Paper,
  Stack,
  FileInput,
  Image,
  Loader,
} from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import {
  getVolunteerSession,
  updateVolunteerSession,
  hasVolunteerSession,
} from '@/lib/volunteerSession';
import { useMediaQuery } from '@mantine/hooks';

export const Sign = () => {
  const sigRef = useRef<SignatureCanvas>(null);
  const [uploadedSignature, setUploadedSignature] = useState<string | null>(
    null
  );
  const [uploadError, setUploadError] = useState<string | null>(null);
  const volunteerId = useQueryParam('volunteerId');
  const navigate = useNavigate();

  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 600px)');
  const [contractUrl, setContractUrl] = useState<string | null>(null);
  const [contractError, setContractError] = useState(false);

  // Check if we have a valid session for this volunteer
  const hasValidSession = volunteerId && hasVolunteerSession(volunteerId);
  const [session] = useState(() => getVolunteerSession());

  useEffect(() => {
    if (!session) return;
    let objectUrl: string | null = null;

    volunteerApi
      .previewContract(session.application)
      .then(blob => {
        objectUrl = URL.createObjectURL(blob);
        setContractUrl(objectUrl);
      })
      .catch(() => setContractError(true));

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [session]);

  const handleSignatureUpload = (file: File | null) => {
    setUploadError(null);

    if (!file) {
      setUploadedSignature(null);
      return;
    }

    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      setUploadedSignature(null);
      setUploadError(t('sign.uploadInvalidType'));
      return;
    }

    if (file.size > 1024 * 1024) {
      setUploadedSignature(null);
      setUploadError(t('sign.uploadTooLarge'));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;

      if (typeof dataUrl === 'string') {
        setUploadedSignature(dataUrl);
      } else {
        setUploadError(t('sign.uploadReadError'));
      }
    };
    reader.onerror = () => {
      setUploadError(t('sign.uploadReadError'));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = () => {
    const drawnSignature = sigRef.current?.isEmpty()
      ? null
      : sigRef.current?.toDataURL('image/png');
    const signatureData = uploadedSignature ?? drawnSignature;

    if (signatureData) {
      updateVolunteerSession({
        contractSigned: true,
        contractSignedAt: new Date().toISOString(),
        signatureData,
      });
      navigate(`/volunteer/create-account?volunteerId=${volunteerId}`);
    } else {
      setUploadError(t('sign.signatureRequired'));
    }
  };

  // If no valid session, show error
  if (!hasValidSession || !session) {
    return (
      <Container size="sm" py="xl">
        <Alert color="red" title={t('sign.error.title')}>
          {t('sign.error.noSession')}
        </Alert>
      </Container>
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Signature = SignatureCanvas as any;

  return (
    <Container size="lg" py="xl">
      <Title order={3}>{t('sign.title')}</Title>
      <Text my={8}>{t('sign.subtitle')}</Text>
      {contractError ? (
        <Alert color="red" title={t('sign.error.title')}>
          {t('sign.error.contractLoad')}
        </Alert>
      ) : !contractUrl ? (
        <Group justify="center" my="xl">
          <Loader />
        </Group>
      ) : isMobile ? (
        <Paper withBorder p="md" my="md">
          <Stack gap="md" align="center">
            <Text ta="center">{t('sign.mobileContractHint')}</Text>
            <Button
              component="a"
              href={contractUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="filled"
            >
              {t('sign.openContract')}
            </Button>
          </Stack>
        </Paper>
      ) : (
        <iframe
          src={contractUrl}
          width="100%"
          style={{ height: '60vh' }}
          title="Contract"
        ></iframe>
      )}
      <div
        style={{
          marginTop: '20px',
          padding: '10px',
        }}
      >
        <Text mb={8}>{t('sign.signatureLabel')}</Text>
        <Signature
          ref={sigRef}
          penColor="black"
          canvasProps={{
            width: isMobile ? 300 : 500,
            height: isMobile ? 150 : 200,
            className: 'sigCanvas',
            style: {
              border: '1px solid #ccc',
              margin: '0 auto',
              display: 'block',
            },
          }}
        />
        <FileInput
          mt="md"
          label={t('sign.uploadSignatureLabel')}
          placeholder={t('sign.uploadSignaturePlaceholder')}
          accept="image/png,image/jpeg"
          clearable
          error={uploadError}
          onChange={handleSignatureUpload}
        />
        {uploadedSignature && (
          <Paper withBorder p="sm" mt="sm">
            <Text size="sm" mb="xs">
              {t('sign.uploadPreview')}
            </Text>
            <Image
              src={uploadedSignature}
              alt={t('sign.uploadPreview')}
              fit="contain"
              h={120}
            />
          </Paper>
        )}

        <Group justify="center" mt="xl">
          <Button
            type="submit"
            size="lg"
            variant="filled"
            onClick={handleSubmit}
          >
            {t('common.next')}
          </Button>
        </Group>
      </div>
    </Container>
  );
};
